import { useState } from "react";
import Card from "@/components/Card";
import Badge from "@/components/Badge";
import Button from "@/components/Button";
import PageHeader from "@/components/PageHeader";
import { useFetch } from "@/lib/api/useFetch";
import type { components } from "@/lib/api/gen/schema";

type AuditEvent = components["schemas"]["AuditEvent"];

export default function Audit() {
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const { data, error, loading } = useFetch<{
    items: AuditEvent[];
    next_cursor?: string;
  }>(
    (api) =>
      api.GET("/audit", {
        params: {
          query: {
            actor: actor || undefined,
            action: action || undefined,
            cursor,
            limit: 25,
          },
        },
      }),
    [actor, action, cursor],
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Audit" subtitle="Append-only record of every administrative change." />

      <Card>
        <div className="mb-4 flex gap-3">
          <input
            placeholder="filter by actor"
            className="flex-1 rounded border border-border bg-surface px-3 py-1.5 text-sm"
            value={actor}
            onChange={(e) => {
              setActor(e.target.value);
              setCursor(undefined);
              setCursorStack([]);
            }}
          />
          <input
            placeholder="filter by action (e.g. policy.create)"
            className="flex-1 rounded border border-border bg-surface px-3 py-1.5 text-sm"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setCursor(undefined);
              setCursorStack([]);
            }}
          />
        </div>

        {loading ? (
          <div className="text-sm text-text-secondary">Loading…</div>
        ) : error ? (
          <div className="text-sm text-danger">{error}</div>
        ) : !data?.items.length ? (
          <div className="text-sm text-text-secondary">No events match the current filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-text-muted">
              <tr>
                <th className="py-2">When</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Resource</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.items.map((e) => (
                <tr key={e.id}>
                  <td className="py-2 font-mono text-xs text-text-muted">
                    {new Date(e.ts).toLocaleString()}
                  </td>
                  <td className="font-mono text-xs">{e.action}</td>
                  <td className="truncate">{e.actor}</td>
                  <td className="truncate font-mono text-xs text-text-secondary">
                    {e.resource}
                  </td>
                  <td>
                    <Badge variant={e.result === "ok" ? "success" : "danger"}>
                      {e.result === "ok" ? "Success" : "Failed"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="mt-4 flex justify-between">
          <Button
            variant="secondary"
            size="sm"
            disabled={cursorStack.length === 0}
            onClick={() => {
              const stack = cursorStack.slice(0, -1);
              setCursor(stack[stack.length - 1]);
              setCursorStack(stack);
            }}
          >
            ← Prev
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!data?.next_cursor}
            onClick={() => {
              if (data?.next_cursor) {
                setCursorStack([...cursorStack, data.next_cursor]);
                setCursor(data.next_cursor);
              }
            }}
          >
            Next →
          </Button>
        </div>
      </Card>
    </div>
  );
}
